import React, { useState } from "react";
import { ChevronDown, Lock } from "lucide-react";
import { BeaconMedia } from "../pages/BeaconMedia";

const Cover = ({ s, accent, font }) => s.cover
  ? <BeaconMedia path={s.cover} type="image" className="w-full h-full object-cover" />
  : <div className="w-full h-full flex items-center justify-center text-3xl" style={{ background: `linear-gradient(135deg, ${accent}40, ${accent}12)`, color: accent, fontFamily: font }}>{(s.title || "?").trim().charAt(0).toUpperCase()}</div>;

const Meta = ({ s, entitled }) => {
  const n = s.items.length;
  const art = (n === 0 && s.layout && s.layout !== "list") || (n > 0 && s.items.every((it) => it.type === "artwork"));
  const free = s.items.filter((it) => it.is_preview).length;
  const locked = !entitled && s.items.some((it) => it.locked);
  return (
    <span className="flex items-center gap-1.5 font-body text-[11px] text-zinc-500">
      {n} {art ? (n === 1 ? "piece" : "pieces") : (n === 1 ? "lesson" : "lessons")}{!entitled && free > 0 && <> · {free} free</>}{locked && <Lock size={11} className="ml-0.5" />}
    </span>
  );
};

const Card = ({ s, open, onToggle, look, entitled, renderItem, renderSection }) => (
  <div className="border bg-[#121212] transition-colors" style={{ borderColor: open ? look.accent + "88" : "rgba(255,255,255,0.1)" }} data-testid={`beacon-store-section-${s.id}`}>
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`beacon-section-toggle-${s.id}`}
      className="w-full flex items-center gap-4 p-3 sm:p-4 text-left hover:bg-white/[0.03] transition-colors">
      <div className="w-20 h-20 sm:w-28 sm:h-20 shrink-0 overflow-hidden rounded-sm"><Cover s={s} accent={look.accent} font={look.sFont} /></div>
      <div className="flex-1 min-w-0">
        <h2 className={`${look.sSize} leading-tight break-words`} style={{ fontFamily: look.sFont, color: look.sColor }} data-testid={`beacon-store-section-title-${s.id}`}>{s.title}</h2>
        {s.description && <p className="font-body text-xs sm:text-sm text-zinc-400 mt-1 line-clamp-2" data-testid={`beacon-store-section-desc-${s.id}`}>{s.description}</p>}
        <div className="mt-1.5"><Meta s={s} entitled={entitled} /></div>
      </div>
      <ChevronDown size={20} className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: look.accent }} />
    </button>
    <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
      <div className="overflow-hidden">
        <div className="px-3 sm:px-4 pb-3" data-testid={`beacon-section-items-${s.id}`}>
          {open && (s.items.length ? ((renderSection && renderSection(s)) || s.items.map((it) => renderItem(it, s))) : <p className="font-body text-xs text-zinc-600 py-3 border-t border-white/5">Lessons coming soon.</p>)}
        </div>
      </div>
    </div>
  </div>
);

// Viewer-facing: sections as tappable cards; one open at a time, all closed on arrival.
export const BeaconSectionCards = ({ sections, look, entitled, renderItem, renderSection, initialOpenId = null }) => {
  const [openId, setOpenId] = useState(initialOpenId);
  return (
    <div className="space-y-3" data-testid="beacon-section-cards">
      {sections.map((s) => <Card key={s.id} s={s} open={openId === s.id} onToggle={() => setOpenId((o) => (o === s.id ? null : s.id))} look={look} entitled={entitled} renderItem={renderItem} renderSection={renderSection} />)}
    </div>
  );
};
